import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle, Info } from "lucide-react";
import { useState, useCallback } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import GrupoMemberListSection, { type GrupoMember } from "./GrupoMemberListSection";

import LinkGrupoMemberDrawer from "./LinkGrupoMemberDrawer";
import UnlinkGrupoModal from "./UnlinkGrupoModal";

interface GrupoEconomicoTabProps {
  /**
   * ID da empresa (undefined = não salva)
   */
  companyId?: number;

  /**
   * Se a empresa é controladora
   */
  isControladora?: boolean;

  /**
   * ID da controladora (se empresa é filial)
   */
  controladoraId?: number;

  /**
   * Nome da controladora (para exibição)
   */
  controladoraNome?: string;
}

/**
 * GrupoEconomicoTab
 *
 * Integração completa com tRPC para gerenciar grupo econômico.
 * Renderiza diferentes estados conforme contexto:
 * - Não salva: bloqueio
 * - Não controladora: informativo
 * - Controladora: listagem de membros + ações (criar, vincular, remover)
 */
export default function GrupoEconomicoTab({
  companyId,
  isControladora = false,
  controladoraId,
  controladoraNome,
}: GrupoEconomicoTabProps) {
  // Estados para drawers/modal

  const [showLinkDrawer, setShowLinkDrawer] = useState(false);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<GrupoMember | null>(null);

  // Query: Listar membros do grupo (somente se controladora)
  const { data: membersData, isLoading, isError, error, refetch } = trpc.company.group.listMembers.useQuery(
    isControladora && companyId
      ? { controladoraId: companyId }
      : { skip: true } as any,
    {
      enabled: isControladora && !!companyId,
      staleTime: 0, // V83 FIX: sem cache para evitar dados stale entre usuários
      retry: false, // Não tentar novamente em 401/403
    }
  );

  // V83: Extrair código de erro para mensagem mais precisa
  const errorCode = (error as any)?.data?.code;
  const isPermissionError = errorCode === 'UNAUTHORIZED' || errorCode === 'FORBIDDEN';

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleLinkExisting = useCallback(() => {
    setShowLinkDrawer(true);
  }, []);

  const handleUnlink = useCallback((member: GrupoMember) => {
    setSelectedMember(member);
    setShowUnlinkModal(true);
  }, []);

  const [, navigate] = useLocation();

  const handleOpen = useCallback((member: GrupoMember) => {
    // Navegar com query params para contexto de grupo
    if (companyId) {
      navigate(`/clients/${member.id}?parentId=${companyId}&parentTab=grupo`);
    } else {
      navigate(`/clients/${member.id}`);
    }
  }, [companyId, navigate]);

  const handleLinkSuccess = useCallback(() => {
    setShowLinkDrawer(false);
    refetch();
  }, [refetch]);

  const handleUnlinkSuccess = useCallback(() => {
    setShowUnlinkModal(false);
    setSelectedMember(null);
    refetch();
  }, [refetch]);

  // Estado 1: Empresa não salva
  if (!companyId) {
    return (
      <div className="space-y-4 p-4">
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            Salve a empresa primeiro para continuar com a gestão de grupo econômico.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Estado 2: Empresa não é controladora
  if (!isControladora) {
    return (
      <div className="space-y-4 p-4">
        <Alert className="border-blue-200 bg-blue-50">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            Gestão de grupo econômico está disponível apenas na empresa Controladora.
          </AlertDescription>
        </Alert>

        {controladoraId && controladoraNome && (
          <Alert className="border-gray-200 bg-gray-50">
            <Info className="h-4 w-4 text-gray-600" />
            <AlertDescription className="text-gray-800">
              Esta empresa pertence ao grupo da controladora{" "}
              <strong>{controladoraNome}</strong>.
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  // Estado 3: Empresa é controladora (funcional)
  return (
    <div className="space-y-4 p-4">
      <Alert className="border-green-200 bg-green-50">
        <Info className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          <strong>Gestão de Grupo Econômico</strong> — Gerencie os membros deste grupo econômico abaixo.
        </AlertDescription>
      </Alert>

      <GrupoMemberListSection
        members={(membersData?.members || []) as GrupoMember[]}
        isLoading={isLoading}
        isError={isError}
        isPermissionError={isPermissionError}
        onRetry={handleRetry}
        onLinkExisting={handleLinkExisting}
        onUnlink={handleUnlink}
        onOpen={handleOpen}
      />

      {/* Drawers e Modal */}
      {companyId && (
        <>
          <LinkGrupoMemberDrawer
            open={showLinkDrawer}
            onOpenChange={setShowLinkDrawer}
            controladoraId={companyId}
            onSuccess={handleLinkSuccess}
          />

          <UnlinkGrupoModal
            open={showUnlinkModal}
            onOpenChange={setShowUnlinkModal}
            member={selectedMember}
            controladoraId={companyId}
            onSuccess={handleUnlinkSuccess}
          />
        </>
      )}
    </div>
  );
}
